import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';

/**
 * Crear sitio — S77.
 *
 * Antes esta pantalla ofrecía cuatro caminos: plantilla, en blanco, generar con
 * IA, o importar. Tres se retiraron. Queda uno solo: se crea el sitio con una
 * página en blanco que ya abre, y desde el editor se sube el .zip o se escribe
 * el código. Menos decisiones, y ningún camino que prometa algo que no cumple.
 */
const slugify = (s) => s
  .toLowerCase()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 60);

export default function WebSiteNew() {
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const onName = (v) => {
    setName(v);
    if (!slugTouched) setSlug(slugify(v));
  };

  const create = async () => {
    if (!name.trim()) { toast.error('Ponle un nombre a tu sitio'); return; }
    if (!slug.trim()) { toast.error('Falta la dirección del sitio'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/tenant/web/sites', {
        name: name.trim(), slug: slug.trim(),
      });
      toast.success('Sitio creado');
      nav(`/tenant/web/sites/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-xl mx-auto space-y-5">
      <Link to="/tenant/web" className="text-xs text-[#F72585]">← Sitio Web</Link>

      <div>
        <h1 className="text-2xl font-bold text-[#0F172A]">Crear sitio</h1>
        <p className="text-sm text-[#64748B] mt-1">
          Empieza con una página lista y en blanco. Después subes tu sitio en un
          .zip o escribes el código desde el editor.
        </p>
      </div>

      <div className="space-y-4 border border-[#E2E8F0] rounded-xl bg-white p-5">
        <div>
          <label htmlFor="site-name" className="text-sm font-medium text-[#0F172A]">
            Nombre del sitio
          </label>
          <Input id="site-name" value={name} onChange={(e) => onName(e.target.value)}
                 placeholder="Salón BEBE" className="mt-1" data-testid="web-new-name" />
          <p className="text-xs text-[#94A3B8] mt-1">
            Solo para que lo identifiques aquí adentro.
          </p>
        </div>

        <div>
          <label htmlFor="site-slug" className="text-sm font-medium text-[#0F172A]">
            Dirección
          </label>
          <Input id="site-slug" value={slug}
                 onChange={(e) => { setSlugTouched(true); setSlug(slugify(e.target.value)); }}
                 placeholder="salon-bebe" className="mt-1 font-mono text-sm"
                 data-testid="web-new-slug" />
          <p className="text-xs text-[#94A3B8] mt-1">
            Se usa mientras conectas tu dominio propio. Solo minúsculas, números
            y guiones.
          </p>
        </div>

        <Button onClick={create} disabled={saving}
                className="w-full bg-[#F72585] hover:bg-[#D81B70]" data-testid="web-new-submit">
          {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
          Crear sitio
        </Button>
      </div>
    </div>
  );
}
